// SerenityConsult's building blocks for the Improvement area (styles in ser.css): the page head, windows, the KPI
// strip, sortable tables, effort bars and the cost calculator.
import { useMemo, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { money, moneyEach, qty } from "./impApi";
import "./ser.css";

export function Head({ kicker, title, lede, children }: { kicker: ReactNode; title: ReactNode; lede?: ReactNode; children?: ReactNode }) {
  return (
    <div className="s-head">
      <span className="s-kicker">{kicker}</span>
      <h1 className="s-h1">{title}</h1>
      {lede && <p className="s-lede">{lede}</p>}
      {children}
    </div>
  );
}

export function Win({ title, sub, tag, actions, children, className = "", id }: { title: ReactNode; sub?: ReactNode; tag?: ReactNode; actions?: ReactNode; children: ReactNode; className?: string; id?: string }) {
  return (
    <section className={`win ${className}`} id={id}>
      <div className="win-bar">
        <div className="win-title">{title}{sub && <span>{sub}</span>}</div>
        {(tag || actions) && <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>{actions}{tag && <span className="tag">{tag}</span>}</div>}
      </div>
      {children}
    </section>
  );
}

export type Kpi = { label: string; value: ReactNode; sub?: ReactNode; tone?: "up" | "dn" };
export function Kpis({ items, boxed = true }: { items: Kpi[]; boxed?: boolean }) {
  return (
    <dl className={`kpis ${boxed ? "boxed" : ""}`}>
      {items.map((k) => <div key={k.label} className="kpi"><dt>{k.label}</dt><dd>{k.value}</dd>{k.sub && <small className={k.tone ?? ""}>{k.sub}</small>}</div>)}
    </dl>
  );
}

/** Segmented buttons: one of a few views. */
export function Seg<T extends string>({ value, options, onChange, label }: { value: T; options: [T, string][]; onChange: (v: T) => void; label: string }) {
  return (
    <div className="seg" role="group" aria-label={label}>
      {options.map(([v, n]) => <button key={v} type="button" aria-pressed={value === v} onClick={() => onChange(v)}>{n}</button>)}
    </div>
  );
}

/** A table that sorts by the column you tap: numbers start high to low, words A to Z. */
export function useSort<T>(rows: T[], keys: Record<string, (r: T) => string | number | null>, initial: { key: string; dir: "asc" | "desc" }) {
  const [sort, setSort] = useState(initial);
  const sorted = useMemo(() => {
    const f = keys[sort.key];
    if (!f) return rows;
    return [...rows].sort((a, b) => {
      const x = f(a), y = f(b);
      if (x === null && y === null) return 0;
      if (x === null) return 1;
      if (y === null) return -1;
      const r = typeof x === "number" && typeof y === "number" ? x - y : String(x).localeCompare(String(y));
      return sort.dir === "asc" ? r : -r;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, sort]);
  const th = (key: string, label: string, opts: { r?: boolean; text?: boolean; className?: string } = {}) => (
    <th key={key} className={`${opts.r ? "r" : ""} ${opts.className ?? ""}`} aria-sort={sort.key === key ? (sort.dir === "asc" ? "ascending" : "descending") : undefined}>
      <button type="button" className="th-sort" onClick={() => setSort((s) => ({ key, dir: s.key === key ? (s.dir === "asc" ? "desc" : "asc") : opts.text ? "asc" : "desc" }))}>{label}</button>
    </th>
  );
  return { sorted, th };
}

/** Effort as three bars: small, medium, large. */
export function Eff({ effort }: { effort: string | null }) {
  const n = effort === "small" ? 1 : effort === "medium" ? 2 : effort === "large" ? 3 : 0;
  return <span className="eff" role="img" aria-label={`Effort: ${effort ?? "not known"}`} title={`Effort: ${effort ?? "not known yet"}`}>{[1, 2, 3].map((k) => <i key={k} className={k <= n ? "on" : ""} />)}</span>;
}

/** An inline bar with its amount, scaled to the largest in the list. */
export function Bar({ value, max, width = 70, amber = false }: { value: number | null; max: number; width?: number; amber?: boolean }) {
  if (value === null) return <span className="muted">Not costed</span>;
  return <div className={`bar ${amber ? "amber" : ""}`}><i style={{ width: Math.max(4, Math.round((value / Math.max(1, max)) * width)) }} /><span>{money(value)}</span></div>;
}

/**
 * The cost of a process, and what taking time out of it frees: drag the sliders. Starts from a process's own numbers
 * when given; "Put it on the roadmap" opens a new opportunity with the saving as its value a year.
 */
export function CostCalculator({ start, process, title = "Cost one of your own" }: {
  start: { volume: number; minutes: number; rate: number; cut?: number };
  process?: { number: string; name: string; area: string | null; minutesEach: number | null };
  title?: string;
}) {
  const [v, setV] = useState({ volume: start.volume, minutes: start.minutes, rate: start.rate, cut: start.cut ?? 30 });
  const each = (v.minutes / 60) * v.rate, year = each * v.volume * 12, freed = (year * v.cut) / 100;
  const set = (k: keyof typeof v) => (e: { target: { value: string } }) => setV({ ...v, [k]: Number(e.target.value) });
  const link = process
    ? `/factory/new?start=roadmap&process=${process.number}${process.area ? `&area=${process.area}` : ""}&value=${Math.round(freed)}&current=${v.minutes}&target=${Math.round(v.minutes * (1 - v.cut / 100) * 10) / 10}`
    : null;
  return (
    <Win title={title} tag="Drag the sliders">
      <div className="win-body">
        <div className="sliders">
          <div className="sl"><label htmlFor="c-vol">Transactions a month</label><output htmlFor="c-vol">{qty(v.volume)}</output><input id="c-vol" type="range" min={1} max={Math.max(5000, start.volume * 2)} step={1} value={v.volume} onChange={set("volume")} /></div>
          <div className="sl"><label htmlFor="c-min">Minutes of hands-on time</label><output htmlFor="c-min">{v.minutes}</output><input id="c-min" type="range" min={1} max={Math.max(120, start.minutes * 2)} step={1} value={v.minutes} onChange={set("minutes")} /></div>
          <div className="sl"><label htmlFor="c-rate">Cost an hour</label><output htmlFor="c-rate">${v.rate}</output><input id="c-rate" type="range" min={15} max={150} step={1} value={v.rate} onChange={set("rate")} /></div>
          <div className="sl"><label htmlFor="c-cut">Hands-on time taken out</label><output htmlFor="c-cut">{v.cut}%</output><input id="c-cut" type="range" min={0} max={90} step={5} value={v.cut} onChange={set("cut")} /></div>
        </div>
        <dl className="calc-out" aria-live="polite">
          <div><dt>Each transaction</dt><dd>{moneyEach(Math.round(each * 100) / 100)}</dd></div>
          <div><dt>A year</dt><dd>{money(year)}</dd></div>
          <div className="save"><dt>Freed a year</dt><dd>{money(freed)}</dd></div>
        </dl>
        {link && <div style={{ marginTop: 14 }}><Link className="s-btn p" to={link}>Put it on the roadmap</Link></div>}
      </div>
    </Win>
  );
}
